import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { ReglaSancionAutomatica } from '../../models/types';
import { reglaSancionAutomaticaServiceFirestore } from '../../services/ReglaSancionAutomaticaServiceFirestore';
import { FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaRobot, FaArrowLeft, FaInfoCircle, FaSave, FaTimes, FaCheck } from 'react-icons/fa';
import { theme } from '../../theme';

// Estilos
const Container = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${theme.colors.background.default};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  color: ${theme.colors.primary.main};
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 2.2rem;
  position: relative;
  padding-bottom: 12px;
  display: flex;
  align-items: center;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 16px;
    color: ${theme.colors.secondary.main};
  }
`;

const Button = styled.button`
  background-color: ${theme.colors.primary.main};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  font-size: 1rem;
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    background-color: ${theme.colors.secondary.main};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const BackButton = styled(Button)`
  background-color: ${theme.colors.text.secondary};
  
  &:hover {
    background-color: ${theme.colors.text.primary};
  }
`;

const InfoMessage = styled.div`
  background-color: rgba(16, 19, 37, 0.03);
  border-left: 4px solid ${theme.colors.primary.main};
  color: ${theme.colors.text.primary};
  padding: 16px 20px;
  margin-bottom: 25px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Th = styled.th`
  background-color: ${theme.colors.primary.main};
  color: white;
  padding: 16px;
  text-align: left;
  font-weight: ${theme.typography.fontWeights.semibold};
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary.main};
  cursor: pointer;
  margin-right: 10px;
  transition: all 0.2s ease;
  font-size: 1.1rem;
  
  &:hover {
    color: ${theme.colors.secondary.main};
    transform: scale(1.1);
  }
`;

const StatusBadge = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  background-color: ${props => props.$active ? theme.colors.success.main : theme.colors.text.secondary};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 30px;
  width: 90%;
  max-width: 600px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: ${theme.colors.primary.main};
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
    color: ${theme.colors.secondary.main};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  
  &:hover {
    color: ${theme.colors.secondary.main};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 8px;
  font-weight: 500;
  color: ${theme.colors.text.primary};
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
`;

const SaveButton = styled(Button)`
  background-color: ${theme.colors.success.main};
  
  &:hover {
    background-color: ${theme.colors.success.dark};
  }
`;

const CancelButton = styled(Button)`
  background-color: ${theme.colors.text.secondary};
  
  &:hover {
    background-color: ${theme.colors.text.primary};
  }
`;

const EmptyMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  
  svg {
    font-size: 3rem;
    color: ${theme.colors.text.secondary};
    margin-bottom: 20px;
  }
  
  span {
    color: ${theme.colors.text.secondary};
    font-size: 1.1rem;
    max-width: 500px;
    line-height: 1.6;
  }
`;

const AlertMessage = styled.div<{ $type: 'error' | 'success' }>`
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 6px;
  background-color: ${props => props.$type === 'error' ? 'rgba(228, 49, 46, 0.1)' : 'rgba(46, 106, 228, 0.1)'};
  border-left: 4px solid ${props => props.$type === 'error' ? theme.colors.secondary.main : theme.colors.success.main};
  color: ${props => props.$type === 'error' ? theme.colors.secondary.main : theme.colors.success.main};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
    font-size: 1.2rem;
  }
`;

// Componente principal
const ReglasSancionesAutomaticas: React.FC = () => {
  const navigate = useNavigate();
  const { data, loadData } = useContext(AppContext);
  const [reglas, setReglas] = useState<ReglaSancionAutomatica[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Estado para el modal de edición/creación
  const [showModal, setShowModal] = useState(false);
  const [editingRegla, setEditingRegla] = useState<ReglaSancionAutomatica | null>(null);
  const [formData, setFormData] = useState<Omit<ReglaSancionAutomatica, 'id' | 'fechaCreacion' | 'fechaModificacion'>>({
    nombre: '',
    descripcion: '',
    condicion: 'dnf',
    puntosPenalizacion: -1,
    activa: true,
    parametros: {}
  });
  
  // Cargar reglas al iniciar
  useEffect(() => {
    loadReglas();
  }, []);
  
  // Cargar reglas de sanciones automáticas
  const loadReglas = async () => {
    try {
      setLoading(true);
      const reglasData = await reglaSancionAutomaticaServiceFirestore.getAll();
      setReglas(reglasData);
    } catch (err) {
      console.error('Error al cargar reglas:', err);
      setError('Error al cargar las reglas de sanciones automáticas');
    } finally {
      setLoading(false);
    }
  };
  
  // Abrir modal para crear nueva regla
  const handleOpenCreateModal = () => {
    setEditingRegla(null);
    setFormData({
      nombre: '',
      descripcion: '',
      condicion: 'dnf',
      puntosPenalizacion: -1,
      activa: true,
      parametros: {}
    });
    setShowModal(true);
  };
  
  // Abrir modal para editar regla existente
  const handleOpenEditModal = (regla: ReglaSancionAutomatica) => {
    setEditingRegla(regla);
    setFormData({
      nombre: regla.nombre,
      descripcion: regla.descripcion,
      condicion: regla.condicion,
      puntosPenalizacion: regla.puntosPenalizacion,
      activa: regla.activa,
      parametros: regla.parametros || {}
    });
    setShowModal(true);
  };
  
  // Cerrar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRegla(null);
  };
  
  // Manejar cambios en el formulario
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'puntosPenalizacion') {
      setFormData(prev => ({
        ...prev,
        [name]: parseInt(value)
      }));
    } else if (name === 'activa') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    } else if (name === 'umbralIncidentes' || name === 'umbralSalidasPista') {
      setFormData(prev => ({
        ...prev,
        parametros: {
          ...prev.parametros,
          [name]: parseInt(value)
        }
      }));
    } else if (name === 'otros') {
      setFormData(prev => ({
        ...prev,
        parametros: {
          ...prev.parametros,
          [name]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Guardar regla (crear o actualizar)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    try {
      setLoading(true);
      
      if (editingRegla) {
        // Actualizar regla existente
        const updated = await reglaSancionAutomaticaServiceFirestore.update(editingRegla.id, formData);
        
        if (updated) {
          setSuccess('Regla actualizada correctamente');
          loadReglas();
          handleCloseModal();
        } else {
          setError('Error al actualizar la regla');
        }
      } else {
        // Crear nueva regla
        const newRegla = await reglaSancionAutomaticaServiceFirestore.create({
          ...formData,
          fechaCreacion: new Date().toISOString().split('T')[0]
        });
        
        if (newRegla) {
          setSuccess('Regla creada correctamente');
          loadReglas();
          handleCloseModal();
        } else {
          setError('Error al crear la regla');
        }
      }
    } catch (err) {
      console.error('Error al guardar la regla:', err);
      setError('Error al guardar la regla');
    } finally {
      setLoading(false);
    }
  };
  
  // Eliminar regla
  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta regla? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      setLoading(true);
      const deleted = await reglaSancionAutomaticaServiceFirestore.delete(id);
      
      if (deleted) {
        setSuccess('Regla eliminada correctamente');
        loadReglas();
      } else {
        setError('Error al eliminar la regla');
      }
    } catch (err) {
      console.error('Error al eliminar la regla:', err);
      setError('Error al eliminar la regla');
    } finally {
      setLoading(false);
    }
  };
  
  // Activar/desactivar regla
  const handleToggleActivation = async (id: string, activa: boolean) => {
    try {
      setLoading(true);
      const updated = await reglaSancionAutomaticaServiceFirestore.toggleActivacion(id, !activa);
      
      if (updated) {
        setSuccess(`Regla ${activa ? 'desactivada' : 'activada'} correctamente`);
        loadReglas();
      } else {
        setError(`Error al ${activa ? 'desactivar' : 'activar'} la regla`);
      }
    } catch (err) {
      console.error('Error al cambiar el estado de la regla:', err);
      setError('Error al cambiar el estado de la regla');
    } finally {
      setLoading(false);
    }
  };
  
  // Renderizar parámetros adicionales según la condición seleccionada
  const renderParametrosAdicionales = () => {
    switch (formData.condicion) {
      case 'incidentes':
        return (
          <FormGroup>
            <Label htmlFor="umbralIncidentes">Umbral de incidentes</Label>
            <Input
              type="number"
              id="umbralIncidentes"
              name="umbralIncidentes"
              value={formData.parametros?.umbralIncidentes || 0}
              onChange={handleChange}
              min="0"
            />
            <small>Número de incidentes a partir del cual se aplicará la sanción</small>
          </FormGroup>
        );
        
      case 'salida_pista':
        return (
          <FormGroup>
            <Label htmlFor="umbralSalidasPista">Umbral de salidas de pista</Label>
            <Input
              type="number"
              id="umbralSalidasPista"
              name="umbralSalidasPista"
              value={formData.parametros?.umbralSalidasPista || 0}
              onChange={handleChange}
              min="0"
            />
            <small>Número de salidas de pista a partir del cual se aplicará la sanción</small>
          </FormGroup>
        );
        
      case 'otra':
        return (
          <FormGroup>
            <Label htmlFor="otros">Otros parámetros</Label>
            <TextArea
              id="otros"
              name="otros"
              value={formData.parametros?.otros || ''}
              onChange={handleChange}
              placeholder="Describe los parámetros adicionales para esta regla"
            />
          </FormGroup>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <Container>
      <Header>
        <div>
          <BackButton onClick={() => navigate('/sanciones')}>
            <FaArrowLeft />
            Volver
          </BackButton>
          <Title>
            <FaRobot /> Reglas de Sanciones Automáticas
          </Title>
        </div>
        <Button onClick={handleOpenCreateModal}>
          <FaPlus /> Nueva Regla
        </Button>
      </Header>
      
      <InfoMessage>
        <FaInfoCircle style={{ marginRight: '10px', color: theme.colors.primary.main }} />
        Las reglas de sanciones automáticas permiten aplicar penalizaciones de forma automática a los pilotos según criterios predefinidos.
        Estas reglas se utilizan cuando se aplican sanciones automáticas a una carrera desde la página de sanciones.
      </InfoMessage>
      
      {error && (
        <AlertMessage $type="error">
          <FaTimes />
          {error}
        </AlertMessage>
      )}
      
      {success && (
        <AlertMessage $type="success">
          <FaCheck />
          {success}
        </AlertMessage>
      )}
      
      {reglas.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th>Nombre</Th>
              <Th>Condición</Th>
              <Th>Puntos</Th>
              <Th>Estado</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {reglas.map(regla => (
              <tr key={regla.id}>
                <Td>{regla.nombre}</Td>
                <Td>
                  {(() => {
                    switch (regla.condicion) {
                      case 'dnf': return 'No terminó la carrera (DNF)';
                      case 'no_participacion': return 'No participó';
                      case 'incidentes': return `Incidentes > ${regla.parametros?.umbralIncidentes || 0}`;
                      case 'salida_pista': return `Salidas de pista > ${regla.parametros?.umbralSalidasPista || 0}`;
                      case 'otra': return 'Otra condición';
                      default: return regla.condicion;
                    }
                  })()}
                </Td>
                <Td>{regla.puntosPenalizacion} puntos</Td>
                <Td>
                  <StatusBadge $active={regla.activa}>
                    {regla.activa ? 'Activa' : 'Inactiva'}
                  </StatusBadge>
                </Td>
                <Td>
                  <ActionButton onClick={() => handleOpenEditModal(regla)} title="Editar">
                    <FaEdit />
                  </ActionButton>
                  <ActionButton onClick={() => handleDelete(regla.id)} title="Eliminar">
                    <FaTrash />
                  </ActionButton>
                  <ActionButton onClick={() => handleToggleActivation(regla.id, regla.activa)} title={regla.activa ? 'Desactivar' : 'Activar'}>
                    {regla.activa ? <FaToggleOn /> : <FaToggleOff />}
                  </ActionButton>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyMessage>
          <FaRobot size={40} />
          <span>No hay reglas de sanciones automáticas definidas. Crea una nueva regla para comenzar a aplicar sanciones automáticas.</span>
        </EmptyMessage>
      )}
      
      {/* Modal para crear/editar reglas */}
      {showModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                <FaRobot /> {editingRegla ? 'Editar Regla' : 'Nueva Regla'}
              </ModalTitle>
              <CloseButton onClick={handleCloseModal}>
                <FaTimes />
              </CloseButton>
            </ModalHeader>
            
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="nombre">Nombre</Label>
                <Input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Sanción por DNF"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="descripcion">Descripción</Label>
                <TextArea
                  id="descripcion"
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  required
                  placeholder="Describe la regla y cuándo se aplica"
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="condicion">Condición</Label>
                <Select
                  id="condicion"
                  name="condicion"
                  value={formData.condicion}
                  onChange={handleChange}
                  required
                >
                  <option value="dnf">No terminó la carrera (DNF)</option>
                  <option value="no_participacion">No participó</option>
                  <option value="incidentes">Exceso de incidentes</option>
                  <option value="salida_pista">Exceso de salidas de pista</option>
                  <option value="otra">Otra condición</option>
                </Select>
              </FormGroup>
              
              {/* Parámetros adicionales según la condición */}
              {renderParametrosAdicionales()}
              
              <FormGroup>
                <Label htmlFor="puntosPenalizacion">Puntos de penalización</Label>
                <Input
                  type="number"
                  id="puntosPenalizacion"
                  name="puntosPenalizacion"
                  value={formData.puntosPenalizacion}
                  onChange={handleChange}
                  required
                />
                <small>Usa valores negativos para restar puntos (ej: -1, -2, etc.)</small>
              </FormGroup>
              
              <FormGroup style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Input
                  type="checkbox"
                  id="activa"
                  name="activa"
                  checked={formData.activa}
                  onChange={handleChange}
                  style={{ width: 'auto', marginRight: '10px' }}
                />
                <Label htmlFor="activa" style={{ margin: 0 }}>Regla activa</Label>
              </FormGroup>
              
              <ButtonGroup>
                <CancelButton type="button" onClick={handleCloseModal}>
                  <FaTimes /> Cancelar
                </CancelButton>
                <SaveButton type="submit" disabled={loading}>
                  <FaSave /> {editingRegla ? 'Actualizar' : 'Guardar'}
                </SaveButton>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default ReglasSancionesAutomaticas;
